import type { Format, HomeData, MetricDef, NoBaseline } from "./api"

/** Home number formats: money with cents under $10k, whole dollars above; counts with separators. */
const usd0 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
const usd2 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 })
const int = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 })

export const money = (v: number | null | undefined, cents?: boolean) => {
  if (v == null) return "—"
  const s = (cents ?? Math.abs(v) < 10_000 ? usd2 : usd0).format(Math.abs(v))
  return v < 0 ? `−${s}` : s
}
/** $1.2K / $41.9K / $1.24M: axes and tight spaces. */
export const moneyShort = (v: number) => {
  const a = Math.abs(v)
  const s = a >= 1e6 ? `$${(a / 1e6).toFixed(a >= 1e7 ? 1 : 2)}M` : a >= 1e3 ? `$${(a / 1e3).toFixed(a >= 1e4 ? 1 : 2).replace(/\.?0+$/, "")}K` : `$${Math.round(a)}`
  return v < 0 ? `−${s}` : s
}
export const count = (v: number | null | undefined) => (v == null ? "—" : int.format(v))
export const pct = (v: number | null | undefined, digits = 2) => (v == null ? "—" : `${v.toFixed(digits)}%`)

export function fmt(def: Pick<MetricDef, "format">, v: number | null | undefined, opts: { short?: boolean } = {}) {
  if (v == null) return "—"
  switch (def.format) {
    case "money":
      return opts.short ? moneyShort(v) : money(v)
    case "percent":
      return pct(v)
    case "ratio":
      return `${v.toFixed(2)}×`
    default:
      return count(v)
  }
}
export const fmtAxis = (format: Format) => (v: number) => (format === "money" ? moneyShort(v) : format === "percent" ? `${Math.round(v * 10) / 10}%` : format === "ratio" ? `${v.toFixed(1)}×` : count(v))

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const ymd = (s: string) => s.split("-").map(Number) as [number, number, number]
export const dayLabel = (s: string) => {
  const [, m, d] = ymd(s)
  return `${MON[m - 1]} ${d}`
}
export const dayLong = (s: string) => {
  const [y, m, d] = ymd(s)
  return `${DOW[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${MON[m - 1]} ${d}`
}
export const hourLabel = (h: string) => {
  const n = Number(h)
  return n === 0 ? "12 AM" : n < 12 ? `${n} AM` : n === 12 ? "12 PM" : `${n - 12} PM`
}
/** "WED · NOV 4, 2026" in the store's time zone. */
export function todayEyebrow(tz: string, now = new Date()) {
  const p = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short", month: "short", day: "numeric", year: "numeric" }).formatToParts(now)
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? ""
  return `${g("weekday")} · ${g("month")} ${g("day")}, ${g("year")}`.toUpperCase()
}
export function greeting(tz: string, name?: string | null, now = new Date()) {
  const h = Number(new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "2-digit", hourCycle: "h23" }).format(now))
  const part = h < 5 ? "Good evening" : h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"
  return name ? `${part}, ${name}` : part
}
/** "3m ago", "2h ago", "Nov 3" */
export function ago(iso: string | null | undefined, now = Date.now()) {
  if (!iso) return null
  const s = Math.max(0, (now - Date.parse(iso)) / 1000)
  if (s < 60) return "just now"
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}
export const timeOfDay = (iso: string, tz: string) => new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }).format(new Date(iso))
/** Jaime S. */
export const shortName = (full: string | null | undefined) => {
  const parts = (full ?? "").trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return null
  return parts.length === 1 ? parts[0] : `${parts[0]} ${parts[parts.length - 1][0]}.`
}

/** "Nov 2" from a YYYY-MM-DD store day. */
export const launchLabel = (d: string | null | undefined) => (d ? dayLabel(d) : null)

/** The tooltip behind "No baseline": why there is no % against the comparison period. */
export function noBaselineHint(reason: NoBaseline, w: HomeData["window"]) {
  const period = w.baseline_detail ? `the comparison period (${w.baseline_detail})` : "the comparison period"
  const launched = launchLabel(w.launch?.date)
  if (reason === "pre_launch") return `No baseline: ${period} is before the store launched${launched ? ` on ${launched}` : ""}, so there is nothing to compare with.`
  if (reason === "partial") return `No baseline: ${period} starts before the store launched${launched ? ` on ${launched}` : ""}, so a % against it would mislead. Pick a range after the launch day to compare.`
  return `No baseline: ${period} had nothing to compare with (zero or no data), so no % is shown.`
}
